import { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ProblemTypeSelector from "@/components/ProblemTypeSelector";
import ClarifyingQuestions from "@/components/ClarifyingQuestions";
import EngineAnimation from "@/components/EngineAnimation";
import QuestionsView from "@/components/QuestionsView";
import AnswersPreview from "@/components/AnswersPreview";
import GateForm from "@/components/GateForm";
import UnlockedView from "@/components/UnlockedView";
import OverallFeedback from "@/components/OverallFeedback";
import TectorBridge from "@/components/TectorBridge";
import {
  fetchProblemTypes,
  startCase,
  submitAnswers,
  getCaseStatus,
  unlockCase,
} from "@/lib/api";

const STEPS = {
  INTRO: "intro",
  CLARIFY: "clarify",
  ENGINE: "engine",
  RESULTS: "results",
  UNLOCKED: "unlocked",
};

export default function ScopeGrid() {
  const [step, setStep] = useState(STEPS.INTRO);

  const [problemTypes, setProblemTypes] = useState([]);
  const [problemType, setProblemType] = useState("market_entry");
  const [rawPrompt, setRawPrompt] = useState("");
  const [caseData, setCaseData] = useState(null); // { case_id, clarifying_questions, display_name }
  const [pipelineResult, setPipelineResult] = useState(null); // questions, preview_answers, visible_conflicts
  const [submittedAnswers, setSubmittedAnswers] = useState(null);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(null); // { questions, top_answers }

  const [busy, setBusy] = useState(false);
  const [engineDone, setEngineDone] = useState(false);
  const [engineError, setEngineError] = useState(null);
  const [gateError, setGateError] = useState(null);

  useEffect(() => {
    fetchProblemTypes()
      .then(setProblemTypes)
      .catch(() => toast.error("Couldn't load problem types."));
  }, []);

  const activeType = useMemo(
    () => problemTypes.find((t) => t.id === problemType),
    [problemTypes, problemType]
  );

  const startFlow = async () => {
    if (!rawPrompt.trim() || rawPrompt.trim().length < 12) {
      toast.error("Give us a couple more sentences of context.");
      return;
    }
    setBusy(true);
    try {
      const res = await startCase({
        problem_type: problemType,
        raw_prompt: rawPrompt.trim(),
      });
      setCaseData(res);
      setStep(STEPS.CLARIFY);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const runEngine = async (answers) => {
    setStep(STEPS.ENGINE);
    setEngineDone(false);
    setEngineError(null);
    setBusy(true);
    setSubmittedAnswers(answers);
    try {
      await submitAnswers(caseData.case_id, answers);
      // Poll for questions readiness
      await pollUntilQuestions(caseData.case_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Engine failed.";
      setEngineError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const pollUntilQuestions = async (caseId) => {
    const started = Date.now();
    const maxMs = 4 * 60 * 1000;
    while (Date.now() - started < maxMs) {
      let s;
      try {
        s = await getCaseStatus(caseId);
      } catch (e) {
        await new Promise((r) => setTimeout(r, 2500));
        continue;
      }
      if (s.questions_status === "error") {
        throw new Error(s.error_msg || "Question generation failed.");
      }
      if (s.questions_status === "ready" && s.questions?.length) {
        setPipelineResult({
          case_id: caseId,
          headline_count: s.headline_count,
          questions: s.questions,
          preview_answers: s.preview_answers || [],
          visible_conflicts: s.visible_conflicts || [],
        });
        setEngineDone(true);
        setTimeout(() => setStep(STEPS.RESULTS), 700);
        if (s.top_answers_status !== "ready") {
          setAnswersLoading(true);
          pollAnswersUntilReady(caseId);
        } else {
          setAnswersLoading(false);
        }
        return;
      }
      await new Promise((r) => setTimeout(r, 2500));
    }
    throw new Error("Engine timed out. Please try again.");
  };

  const pollAnswersUntilReady = async (caseId) => {
    const started = Date.now();
    const maxMs = 4 * 60 * 1000;
    while (Date.now() - started < maxMs) {
      try {
        const p = await getCaseStatus(caseId);
        if (p.top_answers_status === "ready") {
          setPipelineResult((prev) =>
            prev
              ? {
                  ...prev,
                  preview_answers: p.preview_answers || [],
                  visible_conflicts: p.visible_conflicts || [],
                }
              : prev
          );
          setAnswersLoading(false);
          return;
        }
        if (p.top_answers_status === "error") {
          setAnswersLoading(false);
          return;
        }
      } catch (e) {
        // ignore transient
      }
      await new Promise((r) => setTimeout(r, 3500));
    }
    setAnswersLoading(false);
  };

  const unlock = async ({ name, email }) => {
    setBusy(true);
    setGateError(null);
    try {
      const res = await unlockCase(caseData.case_id, { name, email });
      setUnlocked(res);
      setStep(STEPS.UNLOCKED);
      // Persistent shareable URL — no reload, just history swap
      try {
        window.history.replaceState(null, "", `/report/${caseData.case_id}`);
      } catch (_) {}
      toast.success("Report unlocked. Every finding, every conflict.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Couldn't unlock.";
      setGateError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep(STEPS.INTRO);
    setRawPrompt("");
    setCaseData(null);
    setPipelineResult(null);
    setUnlocked(null);
    setEngineDone(false);
    setEngineError(null);
    setGateError(null);
    try {
      window.history.replaceState(null, "", "/");
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-[#0B0F10] text-white">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#12181A",
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      />

      <Header onReset={restart} showReset={step !== STEPS.INTRO} />

      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-10 sm:py-14">
        <AnimatePresence mode="wait">
          {step === STEPS.INTRO && (
            <motion.section
              key="intro"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-10"
              data-testid="intro-section"
            >
              <Hero />

              <div className="space-y-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
                  Step 1 · Pick your problem
                </div>
                <ProblemTypeSelector
                  types={problemTypes}
                  value={problemType}
                  onChange={setProblemType}
                />
              </div>

              <div className="space-y-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
                  Now describe it — like you would to a strategy partner
                </div>
                <Textarea
                  value={rawPrompt}
                  onChange={(e) => setRawPrompt(e.target.value)}
                  data-testid="raw-prompt-input"
                  placeholder={
                    activeType?.id === "pricing_strategy"
                      ? "We're re-pricing our mid-market SaaS plan in Europe. Competitor cut list price 20% last quarter and our win rate dipped 8pts…"
                      : activeType?.id === "competitive_assessment"
                      ? "Our biggest rival just launched an AI copilot in the same category. We need to know whether it's a real threat in the enterprise segment…"
                      : activeType?.id === "investment_diligence"
                      ? "Considering a $60m minority stake in a vertical SaaS platform for insurance brokers in Southeast Asia. Reported 40% ARR growth…"
                      : activeType?.id === "gtm_launch"
                      ? "We're launching our new AI compliance product to US mid-market banks in Q3. Need to figure out sales motion and packaging…"
                      : "We're a European industrial software company considering entering the US mid-market segment through a direct sales motion. Target ARR $30M in 3 years…"
                  }
                  className="min-h-[160px] bg-[#12181A] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-[#35D07F]/50 rounded-2xl p-5 leading-relaxed"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-white/40">
                    We'll ask up to 7 quick clarifiers next.
                  </div>
                  <Button
                    onClick={startFlow}
                    disabled={busy}
                    data-testid="start-button"
                    className="rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold h-11 px-6 disabled:opacity-40"
                  >
                    {busy ? "Reading your case…" : "Start"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>

              <FooterCTA />
            </motion.section>
          )}

          {step === STEPS.CLARIFY && caseData && (
            <motion.section
              key="clarify"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              data-testid="clarify-section"
            >
              <ClarifyingQuestions
                questions={caseData.clarifying_questions}
                onSubmit={runEngine}
                submitting={busy}
              />
            </motion.section>
          )}

          {step === STEPS.ENGINE && (
            <motion.section
              key="engine"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              <EngineAnimation done={engineDone} error={engineError} />
              {engineError && (
                <div className="mt-6">
                  <Button
                    onClick={restart}
                    data-testid="engine-restart"
                    variant="outline"
                    className="rounded-full border-white/20 text-white hover:bg-white/5"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Start over
                  </Button>
                </div>
              )}
            </motion.section>
          )}

          {step === STEPS.RESULTS && pipelineResult && (
            <motion.section
              key="results"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-10"
              data-testid="results-section"
            >
              <QuestionsView
                questions={pipelineResult.questions}
                unlocked={false}
                caseId={caseData?.case_id}
                problemType={problemType}
              />
              <AnswersPreview
                questions={pipelineResult.questions}
                previewAnswers={pipelineResult.preview_answers}
                visibleConflicts={pipelineResult.visible_conflicts}
                loading={answersLoading}
                caseId={caseData?.case_id}
                problemType={problemType}
              />
              <TectorBridge caseId={caseData?.case_id} />
              <GateForm
                onUnlock={unlock}
                submitting={busy}
                error={gateError}
                disabled={answersLoading}
              />
              <OverallFeedback
                caseId={caseData?.case_id}
                problemType={problemType}
              />
            </motion.section>
          )}

          {step === STEPS.UNLOCKED && unlocked && (
            <motion.section
              key="unlocked"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              data-testid="unlocked-section"
              className="space-y-10"
            >
              <UnlockedView
                questions={unlocked.questions}
                topAnswers={unlocked.top_answers}
                caseId={caseData?.case_id}
                problemType={problemType}
                clarifyingQuestions={caseData?.clarifying_questions}
                currentAnswers={submittedAnswers || caseData?.answers}
                meta={{
                  display_name: caseData?.display_name,
                }}
                onRerunStarted={(newCaseId) => {
                  window.location.href = `/report/${newCaseId}`;
                }}
              />
              <TectorBridge caseId={caseData?.case_id} />
              <OverallFeedback
                caseId={caseData?.case_id}
                problemType={problemType}
              />
              <div>
                <Button
                  onClick={restart}
                  data-testid="run-another-button"
                  variant="outline"
                  className="rounded-full border-white/20 text-white hover:bg-white/5"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Run another case
                </Button>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      <Footer />
    </div>
  );
}

function Header({ onReset, showReset }) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0F10]/70 border-b border-white/5">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between">
        <button
          onClick={onReset}
          data-testid="logo-home"
          className="flex items-center gap-2.5 text-left"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#35D07F]/15 border border-[#35D07F]/40">
            <span className="h-2 w-2 rounded-sm bg-[#35D07F]" />
          </span>
          <div>
            <div className="font-bold tracking-tight text-white leading-none">
              ScopeGrid
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
              by Tector · beta
            </div>
          </div>
        </button>
        {showReset && (
          <button
            onClick={onReset}
            data-testid="header-reset"
            className="text-xs text-white/60 hover:text-white transition-colors"
          >
            Start over
          </button>
        )}
      </div>
    </header>
  );
}

function Hero() {
  return (
    <div className="relative pt-4">
      <div className="inline-flex items-center gap-2 rounded-full border border-[#35D07F]/40 bg-[#35D07F]/8 text-[#35D07F] text-[11px] font-semibold uppercase tracking-[0.2em] px-3 py-1">
        <Sparkles className="h-3 w-3" strokeWidth={2.5} />
        Free · takes ~2 minutes
      </div>
      <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-white">
        Your next big call{" "}
        <span className="text-[#35D07F]">deserves the right questions</span>.
      </h1>
      <p className="mt-5 text-[#A0AAB2] text-base sm:text-lg leading-relaxed max-w-2xl">
        Describe your business problem. ScopeGrid returns the sharpest 15–20
        research questions — plus real, source-cited answers to the 5 that
        actually move the decision, with conflicts between sources flagged.
      </p>
    </div>
  );
}

function FooterCTA() {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12181A] p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <div className="text-sm text-[#A0AAB2]">
        <span className="text-white font-medium">
          5 answered, conflict-flagged questions
        </span>{" "}
        &gt; 50 unanswered ones. Every time.
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
        Powered by the Tector framework
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 mt-16">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center gap-3 justify-between text-xs text-white/40">
        <div>© {new Date().getFullYear()} Tector · ScopeGrid beta</div>
        <div>
          Trade-secret framework · axes stay server-side · your data isn't sold
        </div>
      </div>
    </footer>
  );
}
